import { formatRelativeTime } from "@/shared/lib/format";
import { useUIStore } from "@/shared/stores/ui-store";
import { Button } from "@/shared/ui/button";
import { ScrollArea } from "@/shared/ui/scroll-area";
import { Circle, Clock, X } from "lucide-react";
import { useEffect } from "react";

interface NotificationsProps {
  onClose?: () => void;
}

export function Notifications({ onClose }: NotificationsProps) {
  const { notifications, selectedNotificationId, setSelectedNotificationId } = useUIStore();

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if ((e.target as HTMLElement).closest("[data-notification-card]") === null) {
        setSelectedNotificationId(null);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [setSelectedNotificationId]);

  return (
    <div className="flex flex-col gap-[12px] bg-card p-[12px]">
      <div className="flex items-center justify-between px-[12px]">
        <h2 className="text-[20px] font-medium leading-[28px] tracking-[-0.6px] text-black">
          Notifications
        </h2>
        {onClose && (
          <Button variant="ghost" size="icon" onClick={onClose} className="h-4 w-4">
            <X className="h-1 w-1 text-gray-500" />
          </Button>
        )}
      </div>

      <ScrollArea className="h-[400px]">
        <div className="pr-4">
          {notifications.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Circle className="mx-auto mb-2 h-8 w-8 opacity-50" />
              <p>No notifications yet</p>
              <p className="text-sm">New notifications will appear here</p>
            </div>
          ) : (
            <div>
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  data-notification-card
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedNotificationId(notification.id);
                  }}
                  className={`flex cursor-pointer items-start border-b border-gray-400 px-[12px] py-[8px] transition-colors hover:bg-gray-100 ${
                    selectedNotificationId === notification.id ? "bg-blue-100" : ""
                  }`}
                >
                  <div className="flex flex-1 flex-col gap-[4px]">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 shrink-0 text-gray-400" />
                      <p className="text-[14px] font-medium leading-[28px] tracking-[-0.42px] text-gray-400">
                        {formatRelativeTime(notification.timestamp)}
                      </p>
                    </div>
                    <p className="text-[16px] font-medium leading-[24px] tracking-[-0.48px] text-gray-950">
                      {notification.title}
                    </p>
                    <p className="text-[14px] font-medium leading-[28px] tracking-[-0.42px] text-gray-500">
                      {notification.details}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
